import type { SmartOLTOperationSchema } from "../../services/smartolt.service.ts";

export const dateTime = (value?: string | null) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return "—";
  }
};

export type SmartOLTTabId = "overview" | "onus" | "provisioning" | "olts" | "commands" | "settings";

export const SMARTOLT_TABS: { id: SmartOLTTabId; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "onus", label: "ONUs" },
  { id: "provisioning", label: "Provisioning" },
  { id: "olts", label: "OLTs" },
  { id: "commands", label: "Commands" },
  { id: "settings", label: "Settings" },
];

export const OPERATION_UI_GROUPS: { id: string; label: string; categories: string[] }[] = [
  { id: "olt_system", label: "OLT & System", categories: ["system_olt", "topology"] },
  { id: "onu_read", label: "ONU Read", categories: ["onu_read_bulk", "onu_read_single", "onu_types"] },
  { id: "onu_actions", label: "ONU Actions", categories: ["operations", "service_toggles"] },
  { id: "authorization", label: "Authorization", categories: ["authorization", "config_presets"] },
  { id: "network", label: "Network / VLAN / WAN", categories: ["network_policy", "service_port_policy"] },
  { id: "identity", label: "Identity & Location", categories: ["identity", "onu_location"] },
];

export const CURATED_OPERATION_KEYS = new Set([
  "get_onu_details_by_external_id",
  "get_onu_status_by_external_id",
  "get_onu_signal_by_external_id",
  "get_onu_signal_graph_by_external_id",
  "get_onu_traffic_graph_by_external_id",
  "reboot_onu_by_external_id",
  "resync_onu_config_by_external_id",
  "enable_onu_by_external_id",
  "disable_onu_by_external_id",
  "get_all_onus_statuses",
  "get_all_onus_signals",
  "get_olts_list",
  "get_all_unconfigured_onus",
  "authorize_onu_using_authorization_preset",
  "apply_configuration_preset_to_onu_by_external_id",
]);

export const FIELD_LABELS: Record<string, string> = {
  onu_external_id: "Unique External ID",
  onu_sn: "Serial Number",
  olt_id: "OLT ID",
  graph_type: "Graph Type",
  onus_external_ids: "ONU IDs (comma-separated, max 50)",
  upload_speed_profile_name: "Upload Speed Profile",
  download_speed_profile_name: "Download Speed Profile",
  preset_id: "Authorization Preset ID",
  vlan: "VLAN ID",
  zone: "Zone",
  board: "Board",
  port: "PON Port",
  onu_number: "ONU Number",
  task_id: "Task ID",
};

export const formatFieldLabel = (field: string) =>
  FIELD_LABELS[field] ||
  field
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

export const operationTitle = (op: SmartOLTOperationSchema) =>
  op.display_name?.trim() ||
  op.key
    .replace(/_by_external_id$/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const groupOperations = (operations: SmartOLTOperationSchema[]) => {
  const grouped: Record<string, SmartOLTOperationSchema[]> = {};
  for (const group of OPERATION_UI_GROUPS) {
    grouped[group.id] = operations.filter((op) => group.categories.includes(op.category));
  }
  grouped.other = operations.filter(
    (op) => !OPERATION_UI_GROUPS.some((g) => g.categories.includes(op.category)),
  );
  return grouped;
};

export type FieldWidgetType = "text" | "select" | "searchable" | "multi_onu" | "boolean";

export type FieldOption = { value: string; label: string; sublabel?: string };

export const GRAPH_TYPE_OPTIONS: FieldOption[] = [
  { value: "hourly", label: "Hourly" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
];

export const STATIC_FIELD_OPTIONS: Record<string, FieldOption[]> = {
  graph_type: GRAPH_TYPE_OPTIONS,
  pon_type: [
    { value: "gpon", label: "GPON" },
    { value: "epon", label: "EPON" },
  ],
  onu_mode: [
    { value: "Routing", label: "Routing" },
    { value: "Bridging", label: "Bridging" },
    { value: "Other", label: "Other" },
  ],
  tag_transform_mode: [
    { value: "default", label: "Default" },
    { value: "translate", label: "Translate" },
    { value: "transparent", label: "Transparent" },
  ],
  configuration_method: [
    { value: "OMCI", label: "OMCI" },
    { value: "TR069", label: "TR069" },
  ],
  ip_protocol: [
    { value: "ipv4", label: "IPv4" },
    { value: "ipv6", label: "IPv6" },
    { value: "ipv4_ipv6", label: "IPv4 + IPv6" },
  ],
  page_size: [
    { value: "50", label: "50" },
    { value: "100", label: "100" },
    { value: "200", label: "200" },
    { value: "500", label: "500" },
  ],
};

export const FIELD_WIDGET_MAP: Record<string, FieldWidgetType> = {
  onu_external_id: "searchable",
  onus_external_ids: "multi_onu",
  olt_id: "select",
  zone_id: "select",
  graph_type: "select",
  zone: "searchable",
  odb: "searchable",
  vlan: "searchable",
  cvlan: "searchable",
  svlan: "searchable",
  upload_speed_profile_name: "searchable",
  download_speed_profile_name: "searchable",
  preset_id: "searchable",
  onu_type: "searchable",
  pon_type: "select",
  onu_mode: "select",
  tag_transform_mode: "select",
  configuration_method: "select",
  ip_protocol: "select",
  board: "searchable",
  port: "searchable",
  onu_number: "searchable",
  page_size: "select",
  enable_max_mac_learn: "boolean",
  dhcp: "boolean",
  for_iptv: "boolean",
  for_mgmt_voip: "boolean",
  dhcp_snooping: "boolean",
  lan_to_lan: "boolean",
  custom_profile: "boolean",
  catv: "boolean",
  allow_custom_profiles: "boolean",
};

export function getFieldWidgetType(field: string): FieldWidgetType {
  if (FIELD_WIDGET_MAP[field]) return FIELD_WIDGET_MAP[field];
  if (field.includes("speed_profile")) return "searchable";
  if (field.endsWith("_id") && field !== "onu_external_id" && field !== "onus_external_ids") {
    return field === "task_id" ? "text" : "searchable";
  }
  return "text";
}
